import BodyText from '@lyra/core/components/BodyText'
import Section, { SectionProps } from '@lyra/core/components/Section'
import Table, { createColumnHelper, TableData } from '@lyra/core/components/Table'
import filterNulls from '@lyra/core/utils/filterNulls'
import formatUSD from '@lyra/core/utils/formatUSD'
import TokenIcon from '@lyra/web/components/common/TokenIcon'
import { MARKET_ICON_SIZE } from '@lyra/web/constants/layout'
import { PageId } from '@lyra/web/constants/pages'
import { CollateralId, tokenConfig, TokenId } from '@lyra/web/constants/tokens'
import useAuth from '@lyra/web/hooks/useAuth'
import useLyraFundingBalances from '@lyra/web/hooks/useLyraFundingBalances'
import useSubaccount from '@lyra/web/hooks/useSubaccount'
import { getPagePath } from '@lyra/web/utils/pages'
import {
  formatTokenBalance,
  formatTokenName,
  formatTokenSymbol,
  getCollateralForToken,
  getDefaultDepositTokenForToken,
  getTokenForCollateral,
  sortTokens,
} from '@lyra/web/utils/tokens'
import { useRouter } from 'next/navigation'
import React, { useMemo } from 'react'
import { XStack, YStack } from 'tamagui'

import DepositButton from '../common/DepositButton'
import TransferButton from '../common/TransferButton'
import WithdrawButton from '../common/WithdrawButton'

type FundingWalletBalanceTableRow = TableData<{
  tokenId: TokenId
  balanceBn: bigint
  value: number
}>

type Props = { showEmptyBalances?: boolean } & SectionProps

export default function BalanceFundingWalletSection({ showEmptyBalances, ...sectionProps }: Props) {
  const { fundingBalances } = useLyraFundingBalances()

  const { subaccount, subaccountDatas } = useSubaccount()

  const columnHelper = createColumnHelper<FundingWalletBalanceTableRow>()

  const router = useRouter()

  const rows = useMemo(() => {
    const subaccountTokenIds = new Set(
      showEmptyBalances
        ? filterNulls(
            Object.values(subaccountDatas).flatMap((sub) => {
              return Object.entries(sub.collateralBalances).map(([key, bal]) =>
                bal.balance > 0 ? getTokenForCollateral(key as CollateralId) : null
              )
            })
          )
        : []
    )

    const fundingAccountTokenIds = new Set(
      Object.entries(fundingBalances)
        .filter(([_0, bal]) => bal.balance > 0)
        .map(([key]) => key as TokenId)
    )

    const tokenIds = sortTokens(
      Array.from(new Set([...subaccountTokenIds, ...fundingAccountTokenIds]))
    )

    return tokenIds
      .map((tokenId) => {
        const balance = fundingBalances[tokenId]
        return {
          ...balance,
          tokenId,
          onPress: () =>
            router.push(
              getPagePath({ page: PageId.Collateral, collateralId: getCollateralForToken(tokenId) })
            ),
        }
      })
      .sort((a, b) => (a.tokenId === TokenId.DRV ? -1 : b.value - a.value))
  }, [fundingBalances, showEmptyBalances, subaccountDatas, router])

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('token', {
        id: 'token',
        header: 'Token',
        meta: {
          minWidth: 150,
        },
        cell: ({
          row: {
            original: { tokenId },
          },
        }) => {
          return (
            <XStack alignItems="center" gap="$1.5">
              <TokenIcon size={MARKET_ICON_SIZE} symbol={tokenId} />
              <YStack>
                <BodyText>{formatTokenSymbol(tokenId)}</BodyText>
                <BodyText color="secondary">{formatTokenName(tokenId)}</BodyText>
              </YStack>
            </XStack>
          )
        },
      }),
      columnHelper.accessor('balances', {
        header: 'Balance',
        meta: {
          minWidth: 100,
          justifyContent: 'flex-start',
        },
        cell: ({
          row: {
            original: { tokenId, balanceBn, value },
          },
        }) => {
          return (
            <YStack>
              <BodyText>{formatTokenBalance(balanceBn, tokenId)}</BodyText>
              {tokenId === TokenId.DRV || tokenId === TokenId.STDRV ? null : (
                <BodyText color="secondary">{formatUSD(value)}</BodyText>
              )}
            </YStack>
          )
        },
      }),
      columnHelper.accessor('actions', {
        header: '',
        meta: {
          minWidth: 250,
          justifyContent: 'flex-end',
          isActionColumn: true,
        },
        cell: ({
          row: {
            original: { tokenId, balanceBn },
          },
        }) => {
          const isEmptyBalance = balanceBn === BigInt(0)
          const isNonTransferrable = !!tokenConfig[tokenId].isNonTransferrable
          const isNonBridgeable = !!tokenConfig[tokenId].isNonBridgeable
          return (
            <XStack gap="$2" onPress={(e) => e.stopPropagation()}>
              <DepositButton
                isDisabled={isNonBridgeable}
                defaultDepositToken={getDefaultDepositTokenForToken(tokenId)}
              />
              <WithdrawButton
                isDisabled={isEmptyBalance || isNonBridgeable}
                defaultToken={tokenId}
                tooltipProps={
                  isEmptyBalance
                    ? {
                        content: `Transfer ${formatTokenSymbol(
                          tokenId
                        )} from your trading account into your funding account to withdraw them from Derive.`,
                      }
                    : undefined
                }
              />
              <TransferButton
                defaultFromSubaccountId="funding"
                defaultToSubaccountId={subaccount?.subaccount_id}
                isDisabled={isEmptyBalance || isNonTransferrable}
                asset={tokenId}
              />
            </XStack>
          )
        },
      }),
    ]
  }, [columnHelper, subaccount])

  const { isAuthenticated } = useAuth()

  return (
    <Section {...sectionProps}>
      <Section.Header
        title="Funding Account"
        subtitle="Your funding acount is used to deposit tokens to and from your trading accounts."
      />
      <Section.Table>
        <Table
          size="lg"
          data={rows}
          columns={columns}
          emptyMessage={isAuthenticated ? 'No balances' : 'Not connected'}
        />
      </Section.Table>
    </Section>
  )
}
